import React, { useEffect, useRef } from 'react'
import { generateUUID } from '@renderer/utils/idUtils'
import { drawStroke, type BrushStroke, type BrushType } from '@renderer/lib/stickers/brush'

export interface BrushSettings {
  type: BrushType
  color: string
  /** Line width as a percentage of strip width (`brushSizePct`). */
  sizePct: number
}

/**
 * The guest's drawing over the strip.
 *
 * Always shows the strokes — under the stickers, as the print bakes them — and,
 * while the Draw tab is open, also takes the finger: one finger draws one
 * stroke. Points are kept as percentages of the strip and drawn with the same
 * `drawStroke` the print uses, so what is drawn here is what prints.
 */
const DrawLayer: React.FC<{
  width: number
  height: number
  strokes: BrushStroke[]
  /** Draw tab open: the layer takes touches. */
  active: boolean
  brush: BrushSettings
  onStroke: (stroke: BrushStroke) => void
}> = ({ width, height, strokes, active, brush, onStroke }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const liveRef = useRef<BrushStroke | null>(null)
  const pointerRef = useRef<number | null>(null)

  const redraw = (): void => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const all = liveRef.current ? [...strokes, liveRef.current] : strokes
    for (const stroke of all) drawStroke(ctx, stroke, canvas.width, canvas.height)
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || width <= 0 || height <= 0) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    // redraw reads the latest strokes; it only needs to run when they change.
    redraw()
  }, [width, height, strokes])

  const pointAt = (e: React.PointerEvent<HTMLDivElement>): { xPct: number; yPct: number } => {
    const rect = e.currentTarget.getBoundingClientRect()
    return {
      xPct: ((e.clientX - rect.left) / rect.width) * 100,
      yPct: ((e.clientY - rect.top) / rect.height) * 100
    }
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden
      />
      {active && (
        <div
          className="absolute inset-0 cursor-crosshair touch-none"
          onPointerDown={(e) => {
            // One finger, one stroke: a second finger landing mid-stroke is ignored.
            if (pointerRef.current !== null) return
            e.stopPropagation()
            pointerRef.current = e.pointerId
            e.currentTarget.setPointerCapture(e.pointerId)
            liveRef.current = {
              id: generateUUID(),
              points: [pointAt(e)],
              color: brush.color,
              sizePct: brush.sizePct,
              brushType: brush.type
            }
            redraw()
          }}
          onPointerMove={(e) => {
            if (e.pointerId !== pointerRef.current || !liveRef.current) return
            liveRef.current.points.push(pointAt(e))
            redraw()
          }}
          onPointerUp={(e) => {
            if (e.pointerId !== pointerRef.current) return
            pointerRef.current = null
            const done = liveRef.current
            liveRef.current = null
            if (done) onStroke(done)
          }}
          onPointerCancel={() => {
            pointerRef.current = null
            liveRef.current = null
            redraw()
          }}
        />
      )}
    </>
  )
}

export default DrawLayer
